<template>
    <div>
        <Breadcrumb :home="breadcrumb_home" :model="breadcrumb_paths" />

        <div class="w-full flex flex-wrap">
            <div class="w-full md:w-1/2 px-2">
                <Fieldset legend="訂單資訊" :toggleable="true">
                    <div class="item">
                        <div class="label">訂單號碼</div>
                        {{ order.order_no }}
                        <template v-if="order.user_id == 0">
                            (訪客訂單)</template
                        >
                    </div>

                    <div class="item">
                        <div class="label">訂單日期</div>
                        {{ formatDate(order.created_at) }}
                    </div>

                    <div class="item">
                        <div class="label">訂單狀態</div>
                        {{ statusString(order.status) }}
                        <Dropdown
                            class="ml-5"
                            :options="batch_action.options"
                            v-model="batch_action.selected"
                            optionLabel="name"
                            optionValue="code"
                            placeholder="變更訂單狀態…"
                            @change="changeStatus"
                        />
                    </div>

                    <div class="item">
                        <div class="label">訂單備註</div>
                        {{ order.comment }}
                    </div>
                </Fieldset>

                <Fieldset legend="訂購人資訊" :toggleable="true">
                    <div class="item">
                        <div class="label">訂購人姓名</div>
                        {{ order.purchaser.name }}
                    </div>

                    <div class="item">
                        <div class="label">連絡電話</div>
                        {{ order.purchaser.phone }}
                    </div>

                    <div class="item">
                        <div class="label">信箱</div>
                        {{ order.purchaser.email }}
                    </div>
                </Fieldset>

                <Fieldset legend="收件人資訊" :toggleable="true">
                    <div class="item">
                        <div class="label">收件人姓名</div>
                        {{ order.recipient.name }}
                    </div>

                    <div class="item">
                        <div class="label">連絡電話</div>
                        {{ order.recipient.phone }}
                    </div>

                    <div class="item">
                        <div class="label">收件地址</div>
                        {{
                            order.recipient.storeaddress ??
                            order.recipient.address
                        }}
                        <span
                            v-if="order.recipient.storename"
                            class="font-bold"
                        >
                            ({{ order.recipient.storename }})
                        </span>
                        <br />
                        <Button
                            label="更改收件門市"
                            class="mt-2"
                            v-if="order.recipient.storeaddress"
                            @click="changePrescoStoreSelection"
                        ></Button>
                    </div>

                    <div class="item">
                        <div class="label">收件時間</div>
                        {{ order.recipient.time }}
                    </div>
                </Fieldset>
            </div>

            <div class="w-full md:w-1/2 px-2">
                <Fieldset legend="購買資訊" :toggleable="true">
                    <div
                        class="mb-2"
                        :key="delivery_type + '_delivery'"
                        v-for="delivery_type in existing_delivery_types"
                    >
                        {{ delivery_types[delivery_type] }}
                        <hr />
                        <div
                            class="flex flex-row mt-1 justify-start items-center gap-x-4 w-full"
                            :key="delivery_type + '_' + index"
                            v-for="(item, index) in order.items_raw[
                                delivery_type
                            ]"
                        >
                            <img
                                v-if="
                                    Array.isArray(item.images) &&
                                    item.images.length > 0
                                "
                                class="object-cover aspect-square product-image"
                                :src="
                                    '/storage/product-images/' +
                                    item.images[0] +
                                    '/50.jpg'
                                "
                                width="50"
                                height="50"
                            />
                            <img
                                v-else
                                class="object-cover aspect-square product-image"
                                src="//dummyimage.com/50/ccc/666?text=%E5%90%83%E8%B2%A8%E5%AE%B6"
                                width="50"
                                height="50"
                            />
                            <div class="flex flex-col w-full">
                                <div>{{ item.name }}</div>
                                <div class="flex flex-row">
                                    <div class="basis-1/4 text-xs">
                                        {{
                                            item.unit_price
                                                | commaFormat
                                                | priceFormat
                                        }}
                                        x
                                        {{ item.quantity }}
                                    </div>
                                    <div
                                        v-if="order.user_id != 0"
                                        class="basis-1/4 text-xs"
                                    >
                                        CV: {{ item.total_cv | commaFormat }}
                                    </div>
                                    <div
                                        v-if="item.type == 'add-on'"
                                        class="basis-1/4 text-xs"
                                    >
                                        （加購品）
                                    </div>
                                </div>
                            </div>
                        </div>

                        <hr class="mt-2" />
                        <div class="flex flex-row justify-start">
                            <div class="basis-1/3">
                                運費
                                {{
                                    order.shipping_per_type[delivery_type]
                                        | commaFormat
                                        | priceFormat
                                }}
                            </div>
                            <div class="basis-1/3">
                                總計
                                {{
                                    order.price_per_type[delivery_type]
                                        | commaFormat
                                        | priceFormat
                                }}
                            </div>
                            <div v-if="order.user_id != 0" class="basis-1/3">
                                總計CV
                                {{
                                    order.cv_per_type[delivery_type]
                                        | commaFormat
                                }}
                            </div>
                        </div>
                    </div>
                    <hr class="mt-5" />
                    <div class="flex flex-col w-full">
                        <div class="flex flex-row justify-between">
                            <div>總計費用</div>
                            <div>
                                {{ order.price | commaFormat | priceFormat }}
                            </div>
                        </div>
                        <div class="flex flex-row justify-between">
                            <div>運費</div>
                            <div>
                                {{ order.shipping | commaFormat | priceFormat }}
                            </div>
                        </div>
                        <div
                            v-if="order.user_id != 0"
                            class="flex flex-row justify-between"
                        >
                            <div>折扣</div>
                            <div>
                                {{ order.discount | commaFormat | priceFormat }}
                            </div>
                        </div>
                        <div
                            v-if="order.user_id != 0"
                            class="flex flex-row justify-between"
                        >
                            <div>購物金抵用</div>
                            <div>
                                {{ order.bonus | commaFormat | priceFormat }}
                            </div>
                        </div>
                        <hr class="mt-5" />
                        <div class="flex flex-row justify-between">
                            <div>總計</div>
                            <div>
                                {{
                                    order.total_price
                                        | commaFormat
                                        | priceFormat
                                }}
                            </div>
                        </div>
                        <div
                            v-if="order.user_id != 0"
                            class="flex flex-row justify-between"
                        >
                            <div>總計CV</div>
                            <div>{{ order.cv | commaFormat }}</div>
                        </div>
                    </div>
                </Fieldset>
                <Fieldset legend="物流資訊" :toggleable="true">
                    <div
                        class="item"
                        :key="delivery_type + '_shipping_code'"
                        v-for="delivery_type in existing_delivery_types"
                    >
                        <div class="flex items-center gap-2">
                            <div class="label">
                                {{ delivery_types[delivery_type] }}
                            </div>
                            <div v-if="delivery_type == 'frozen'">
                                {{
                                    order.recipient.delivery?.frozen ??
                                    "無物流資訊"
                                }}
                            </div>
                            <div v-if="delivery_type == 'refrigerated'">
                                {{
                                    order.recipient.delivery?.refrigerated ??
                                    "無物流資訊"
                                }}
                            </div>
                            <div v-if="delivery_type == 'normal'">
                                {{
                                    order.recipient.delivery?.normal ??
                                    "無物流資訊"
                                }}
                            </div>
                        </div>
                        <div
                            v-if="order.recipient.delivery_status?.frozen"
                            class="ml-5 mb-2"
                        >
                            <div
                                class="text-xs"
                                :key="
                                    delivery_type +
                                    '_' +
                                    status.CreateDateTime +
                                    '_' +
                                    status.StatusId
                                "
                                v-for="status in order.recipient.delivery_status
                                    .frozen.StatusList"
                            >
                                -
                                {{
                                    dayjs(status.CreateDateTime).format(
                                        "YYYY-MM-DD (dd) HH:mm:ss"
                                    )
                                }}
                                {{ status.StationName }}
                                {{ status.StatusName }} ({{ status.StatusId }})
                            </div>
                        </div>
                        <div
                            v-if="order.recipient.delivery_status?.refrigerated"
                            class="ml-5 mb-2"
                        >
                            <div
                                class="text-xs"
                                :key="
                                    delivery_type +
                                    '_' +
                                    status.CreateDateTime +
                                    '_' +
                                    status.StatusId
                                "
                                v-for="status in order.recipient.delivery_status
                                    .refrigerated.StatusList"
                            >
                                -
                                {{
                                    dayjs(status.CreateDateTime).format(
                                        "YYYY-MM-DD (dd) HH:mm:ss"
                                    )
                                }}
                                {{ status.StationName }}
                                {{ status.StatusName }} ({{ status.StatusId }})
                            </div>
                        </div>
                        <div
                            v-if="order.recipient.delivery_status?.normal"
                            class="ml-5 mb-2"
                        >
                            <div
                                class="text-xs"
                                :key="
                                    delivery_type +
                                    '_' +
                                    status.CreateDateTime +
                                    '_' +
                                    status.StatusId
                                "
                                v-for="status in order.recipient.delivery_status
                                    .normal.StatusList"
                            >
                                -
                                {{
                                    dayjs(status.CreateDateTime).format(
                                        "YYYY-MM-DD (dd) HH:mm:ss"
                                    )
                                }}
                                {{ status.StationName }}
                                {{ status.StatusName }} ({{ status.StatusId }})
                            </div>
                        </div>
                    </div>
                </Fieldset>
                <Fieldset legend="訂單進度紀錄" :toggleable="true">
                    <div
                        :key="'history_' + index"
                        v-for="(history, index) in order.history"
                        class="history-item"
                    >
                        <div class="description">{{ history.description }}</div>
                        <div class="timestamp">
                            {{ formatDate(history.timestamp) }}
                        </div>
                    </div>
                </Fieldset>
            </div>
        </div>
    </div>
</template>

<script>
import Layout from "../Layout";
import Breadcrumb from "@/Shared/Components/Breadcrumb";
import Button from "primevue/button";
import Fieldset from "primevue/fieldset";
import Dropdown from "primevue/dropdown";
import DataTable from "primevue/datatable";
import Column from "primevue/column";

export default {
    layout: Layout,
    components: {
        Breadcrumb,
        Fieldset,
        Dropdown,
        DataTable,
        Column,
        Button,
    },
    props: {
        order: Object,
    },
    filters: {
        priceFormat: function (value) {
            return "$" + value;
        },
        commaFormat: function (value) {
            return value
                .toString()
                .replace(
                    /^(-?\d+?)((?:\d{3})+)(?=\.\d+$|$)/,
                    function (all, pre, groupOf3Digital) {
                        return pre + groupOf3Digital.replace(/\d{3}/g, ",$&");
                    }
                );
        },
    },
    computed: {
        existing_delivery_types() {
            return _.keys(this.order.items);
        },
    },
    data() {
        return {
            breadcrumb_home: {
                label: "訂單管理",
                to: this.$page.props.from
                    ? decodeURIComponent(atob(this.$page.props.from))
                    : route("admin.orders"),
            },
            breadcrumb_paths: [
                {
                    label: this.order.order_no,
                    disabled: true,
                },
            ],
            delivery_types: {
                normal: "常溫商品",
                refrigerated: "冷藏商品",
                frozen: "冷凍商品",
            },
            batch_action: {
                options: [
                    {
                        code: 0,
                        name: "未付款",
                    },
                    {
                        code: 1,
                        name: "待確認",
                    },
                    {
                        code: 2,
                        name: "已確認",
                    },
                    {
                        code: 3,
                        name: "備貨中",
                    },
                    {
                        code: 4,
                        name: "準備出貨",
                    },
                    {
                        code: 5,
                        name: "已出貨",
                    },
                    {
                        code: 6,
                        name: "已完成",
                    },
                    {
                        code: -1,
                        name: "申請取消",
                    },
                    {
                        code: -2,
                        name: "已取消",
                    },
                    {
                        code: -3,
                        name: "申請退款",
                    },
                    {
                        code: -4,
                        name: "退款中",
                    },
                    {
                        code: -5,
                        name: "已退款",
                    },
                    {
                        code: -6,
                        name: "貨態異常",
                    },
                ],
                selected: null,
            },
        };
    },
    methods: {
        statusString(statusCode) {
            switch (statusCode) {
                case 0:
                    return "未付款";

                case 1:
                    return "待確認";

                case 2:
                    return "已確認";

                case 3:
                    return "備貨中";

                case 4:
                    return "準備出貨";

                case 5:
                    return "已出貨";

                case 6:
                    return "已完成";

                case -1:
                    return "申請取消";

                case -2:
                    return "已取消";

                case -3:
                    return "申請退款";

                case -4:
                    return "退款中";

                case -5:
                    return "已退款";

                case -6:
                    return "貨態異常";
            }
        },
        formatDate(dateString) {
            let date = new Date(dateString);
            return (
                date.getFullYear() +
                "/" +
                (date.getMonth() + 1).toString().padStart(2, "0") +
                "/" +
                date.getDate().toString().padStart(2, "0") +
                " " +
                date.getHours().toString().padStart(2, "0") +
                ":" +
                date.getMinutes().toString().padStart(2, "0") +
                ":" +
                date.getSeconds().toString().padStart(2, "0")
            );
        },
        changeStatus() {
            let code = this.batch_action.selected;
            this.batch_action.selected = null;
            this.$inertia.post(
                route("admin.orders.batch_set_status"),
                {
                    id: [this.order.id],
                    code: code,
                    from: this.$page.url,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                }
            );
        },
        goToProductPage(slug) {
            this.$inertia.visit(route("product", slug));
        },
        //修改收件門市
        changePrescoStoreSelection() {
            // 跳頁
            window.top.location.href = route("change_presco.select_store", [
                this.$page.props.identifier,
                this.order.recipient.delivery_method === "711_pay"
                    ? "pay"
                    : null,
                this.existing_delivery_types.some((type) => type === "frozen")
                    ? { type: "frozen" }
                    : { type: "regular" },
                { order_no: this.order.order_no },
                { is_admin: true },
            ]);
        },
    },
};
</script>

<style lang="postcss">
.p-breadcrumb {
    @apply border-0;
}

Fieldset {
    @apply my-5;
}

.item {
    @apply my-2;
}

.label {
    @apply text-sm;
}

.history-item {
    @apply p-2 border border-fh-gray-border;
}

.history-item .timestamp {
    @apply text-xs text-fh-gray-light;
}
</style>
